import React, { useEffect, useState, useRef } from "react";
import styled from "styled-components";
import { CSSTransition } from "react-transition-group";
import Button from "../../Generic/Button";
import Card from "../../Generic/Card";
import InputBox from "../../Generic/InputBox";
import SuccessButton from "../../Generic/SuccessButton";
import { successNotification, errorNotification } from "../../Generic/Notifications";
import Loader from "../../Generic/Loader";
import { RecipeSegmentsEdit } from "./RecipeSegmentsEdit";
import { SearchBarWithResults } from "./SearchBarWithResults";
import StudioService from "../Services/StudioService";
import { useNavigate } from "react-router-dom";
import { useRecipe } from "./context/RecipeContext";
import { Patterns } from "./constants";
import { deepCopy } from "../../util/util";
import close from "../../Assets/Close.png";

const FIELDS_TO_IGNORE_ON_IMPORT = ["_id", "name", "description", "note"];

const Page = styled.div`
	height: 100%;
	width: 100%;
	display: flex;
	flex-direction: column;
	align-items: center;
	padding: 1rem;
`;

const Header = styled.div`
	display: flex;
	justify-content: flex-start;
	font-size: 1.6rem;
	margin: ${(props) => props.margin};
	width: 100%;
`;

const Div = styled.div`
	color: ${(props) => props.color};
	position: ${(props) => props.position};
	width: ${(props) => props.width};
	display: flex;
	flex-direction: ${(props) => props.flexDirection};
	flex-wrap: ${(props) => props.flexWrap};
	justify-content: ${(props) => props.jContent};
	align-items: ${(props) => props.alignItem};
	padding: ${(props) => props.padding};
	margin: ${(props) => props.margin};
	text-align: ${(props) => props.txtAlign};
	font-size: ${(props) => props.fontSize};
	font-weight: ${(props) => props.fontWeight};
	font-family: ${(props) => props.fontFamily};
	gap: ${(props) => props.gap};
	background: ${(props) => props.background};
	border-radius: ${(props) => props.borderRadius};
	@media (max-width: 1000px) {
		justify-content: ${(props) => props.jContentBefore1000};
	}
`;

const Span = styled.span`
	font-size: ${(props) => props.fontSize};
	font-weight: ${(props) => props.fontWeight};
	font-family: ${(props) => props.fontFamily};
	color: ${(props) => props.color};
	margin: ${(props) => props.margin};
	padding: ${(props) => props.padding};
`;

// Reusable styled components
const Column = styled(Div)`
	flex-direction: column;
`;

const FieldColumn = styled(Column)`
	/* Column container for a label + input */
`;

const Label = styled(Span)`
	color: rgba(0, 0, 0, 0.6);
`;

const RowWrap = styled(Div)`
	flex-wrap: wrap;
	gap: 1rem;
`;

const ActionRow = styled(Div)`
	justify-content: flex-end;
	gap: 1rem;
	padding: 1rem 0;
`;

const ImportModalOverlay = styled.div`
	position: fixed;
	display: flex;
	justify-content: center;
	align-items: center;
	background: rgba(0, 0, 0, 0.4);
	left: 0;
	top: 0;
	right: 0;
	bottom: 0;
	z-index: 310;
`;

const ImportLoadingOverlay = styled.div`
	position: fixed;
	display: flex;
	justify-content: center;
	align-items: center;
	background: rgba(0, 0, 0, 0.4);
	left: 0;
	top: 0;
	right: 0;
	bottom: 0;
	z-index: 320;
`;

const ImportModal = styled.div`
	display: flex;
	flex-direction: column;
	background: #fff;
	max-height: 90%;
	height: max-content;
	width: 90%;
	max-width: 28rem;
	border-radius: 10px;
	z-index: 311;
`;

// Input wrapper components with standardized props
const DefaultInput = (props) => {
	const { width, breakpoint, widthBeforeBreakpoint, ...rest } = props;
	return <InputBox padding="0.5rem" margin="0.5rem 0 0 0" width={"20rem"} {...rest} />;
};

const RecipeCreate = () => {
	const { editingRecipe, updateRecipe, changeEditMode, setInitialEditingRecipe } = useRecipe();
	const navigate = useNavigate();
	const segmentsRef = useRef(editingRecipe?.segments);
	const [recipeVersion, setRecipeVersion] = useState(0);
	const [showImportSection, setShowImportSection] = useState(false);
	const [selectedRecipeId, setSelectedRecipeId] = useState(null);
	const [importLoading, setImportLoading] = useState(false);

	async function handleImportConfirm() {
		if (!selectedRecipeId) return;
		setImportLoading(true);

		const response = await StudioService.getRecipe(selectedRecipeId);
		const { data, error } = response;
		if (error) {
			errorNotification(error);
			return;
		}
		if (data) {
			const recipe = deepCopy(data);
			for(const key of FIELDS_TO_IGNORE_ON_IMPORT) {
				delete recipe[key];
			}

			setInitialEditingRecipe(recipe);
			setRecipeVersion((v) => v + 1);
			setShowImportSection(false);
			setSelectedRecipeId(null);
		}
		setImportLoading(false);
	}

	async function sendUpdatedData() {
		var payload = {
			...editingRecipe,
			segments: segmentsRef.current ?? editingRecipe?.segments,
			//Hardcoded things which need to be taken as an input later on.
			brandId: "kocafe",
		};
		const response = await StudioService.createRecipe(payload);
		const { data, error } = response;
		if (data) {
			successNotification("Changes saved successfully");
			navigate(`/studio/recipe/${data._id}`);
		}
		if (error) {
			errorNotification(error);
		}
	}

	function updateData(evnt) {
		//Get numeric input for fields if type is set as number
		var payload =
			evnt.target.type === "number"
				? { column: evnt.target.id, value: evnt.target.valueAsNumber }
				: { column: evnt.target.id, value: evnt.target.value };
		if (evnt.target.type === "checkbox") {
			payload = { column: evnt.target.id, value: evnt.target.checked };
		}
		updateRecipe(payload);
	}

	useEffect(() => {
		return () => {
			//Edit mode can be ignored ,just to clear editingRecipe
			changeEditMode({ editMode: false, objectToEdit: {} });
		};
	}, []);

	function renderRecipeCreate(recipe) {
		return (
			<>
				<Card margin="1rem 0 0 0" width="100%" alignItem="center" flexDirection="column">
					<RowWrap flexDirection="row" width="100%" alignItem="center">
						<FieldColumn width="100%">
							<Label>Name</Label>
							<DefaultInput
								id={"name"}
								onChange={(e) => {
									updateData(e);
								}}
								value={recipe?.name}
								width="100%"
							/>
						</FieldColumn>
						<FieldColumn width="100%">
							<Label>Description</Label>
							<DefaultInput
								id={"description"}
								onChange={(e) => {
									updateData(e);
								}}
								value={recipe?.description}
								width="100%"
							/>
						</FieldColumn>
						<FieldColumn width="100%">
							<Label>Note</Label>
							<DefaultInput
								id={"note"}
								onChange={(e) => {
									updateData(e);
								}}
								value={recipe?.note}
								width="100%"
							/>
						</FieldColumn>
						<FieldColumn width="100%" margin="0 0 1rem 0">
							<Label>Dish</Label>
							<Div width="100%" margin="0.5rem 0 0 0">
								<SearchBarWithResults
									colToShow="title"
									colToStore="_id"
									endpoint="/v1/dashboard/dishes/search?page=1&"
									onChange={(val) => {
										updateRecipe({ column: "dishId", value: val });
									}}
								/>
							</Div>
						</FieldColumn>
					</RowWrap>
				</Card>
				<RecipeSegmentsEdit
					segmentsRef={segmentsRef}
					initialSegments={recipe?.segments}
					segmentsSyncKey={recipeVersion}
					needMixing={recipe?.needMixing}
					Patterns={Patterns}
					updatePayload={updateRecipe}
				/>
				<ActionRow>
					<Div>
						<Button
						onClick={() => {
							changeEditMode({ editMode: false, objectToEdit: {} });
							setRecipeVersion((v) => v + 1);
						}}>
						Reset
					</Button>
					</Div>
					<Div>
						<SuccessButton
							onClick={() => {
								sendUpdatedData();
							}}>
							Confirm
						</SuccessButton>
					</Div>
				</ActionRow>
			</>
		);
	}

	return (
		<Page>
			<Div width="100%" alignItem="center" flexDirection="row" jContent="space-between" gap="0.5rem">
				<Header margin="0">Create Recipe</Header>
			</Div>
			<Div width="100%" alignItem="center" flexDirection="row" jContent="flex-end" gap="0.5rem">
				<Button
						onClick={() => setShowImportSection((prev) => !prev)}
					>
						Import an Existing Recipe
					</Button>
			</Div>
			{importLoading && (
				<ImportLoadingOverlay>
					<Loader />
				</ImportLoadingOverlay>
			)}
			<CSSTransition in={showImportSection} timeout={300} classNames="container" unmountOnExit>
				<ImportModalOverlay onClick={() => setShowImportSection(false)}>
					<ImportModal onClick={(e) => e.stopPropagation()}>
						<Div
							borderRadius="10px 10px 0 0"
							color="#fff"
							jContent="space-between"
							alignItem="center"
							width="100%"
							background="rgba(45, 35, 66, 0.8)"
							fontSize="1.2rem"
							padding="1rem">
							<Span>Import Recipe</Span>
							<img
								onClick={() => setShowImportSection(false)}
								src={close}
								alt="close"
								style={{
									height: "1.5rem",
									cursor: "pointer",
									filter: "invert(97%) sepia(0%) saturate(7500%) hue-rotate(126deg) brightness(111%) contrast(100%)",
								}}
							/>
						</Div>
						<Div padding="1rem" flexDirection="column" gap="0.75rem">
							<Label>Search and select a recipe to import</Label>
							<RowWrap flexDirection="row" width="100%" alignItem="flex-end">
								<Div width="100%" style={{ maxWidth: "20rem" }}>
									<SearchBarWithResults
										colToShow="name"
										colToStore="_id"
										endpoint="/v3/dashboard/recipe/search?"
										requestType="post"
										onChange={(id) => setSelectedRecipeId(id)}
									/>
								</Div>
								<SuccessButton onClick={handleImportConfirm} disabled={!selectedRecipeId}>
									Confirm
								</SuccessButton>
							</RowWrap>
						</Div>
					</ImportModal>
				</ImportModalOverlay>
			</CSSTransition>
			{renderRecipeCreate(editingRecipe)}
		</Page>
	);
};

export default RecipeCreate;
